/**
 * Mancala Board Component
 *
 * Horizontal board layout:
 * - Player 2's store on the left
 * - 2 rows of 6 pits in the middle (Player 2 top, Player 1 bottom)
 * - Player 1's store on the right
 *
 * Player 2's pits are displayed right-to-left (index 5,4,3,2,1,0)
 * Player 1's pits are displayed left-to-right (index 0,1,2,3,4,5)
 *
 * Click to select a pit, highlight valid pits, seed count display.
 */

import React, { useCallback } from 'react';
import {
  MancalaBoardProps,
  PITS_PER_SIDE,
} from '../types';
import './MancalaBoard.css';

const MancalaBoard: React.FC<MancalaBoardProps> = ({
  board,
  currentPlayer,
  validPits,
  lastMoveResult,
  onPitClick,
  disabled = false,
  animating = false,
}) => {
  const isValidPit = useCallback(
    (pitIndex: number): boolean => {
      return validPits.includes(pitIndex);
    },
    [validPits]
  );

  const handlePitClick = useCallback(
    (pitIndex: number) => {
      if (disabled || animating) return;
      if (!isValidPit(pitIndex)) return;
      onPitClick(pitIndex);
    },
    [disabled, animating, isValidPit, onPitClick]
  );

  const renderSeeds = (count: number) => {
    if (count === 0) return null;

    // For small counts, show individual seed dots
    if (count <= 12) {
      return (
        <div className="mancala-board__seeds" aria-hidden="true">
          {Array.from({ length: count }, (_, i) => (
            <span key={i} className="mancala-board__seed" />
          ))}
        </div>
      );
    }

    // For larger counts, just show the number
    return null;
  };

  const renderPit = (side: number, pitIndex: number) => {
    const seeds = board.pits[side][pitIndex];
    const isCurrentPlayerSide =
      (currentPlayer === 'player1' && side === 0) ||
      (currentPlayer === 'player2' && side === 1);
    const isValid = isCurrentPlayerSide && isValidPit(pitIndex) && !disabled;
    const wasLastMove =
      lastMoveResult?.move.player === (side === 0 ? 'player1' : 'player2') &&
      lastMoveResult?.move.pitIndex === pitIndex;

    const pitClasses = [
      'mancala-board__pit',
      isValid ? 'mancala-board__pit--valid' : '',
      wasLastMove ? 'mancala-board__pit--last-move' : '',
      seeds === 0 ? 'mancala-board__pit--empty' : '',
    ]
      .filter(Boolean)
      .join(' ');

    const playerLabel = side === 0 ? 'Player 1' : 'Player 2';
    const ariaLabel = isValid
      ? `${playerLabel} pit ${pitIndex + 1}: ${seeds} seeds. Click to sow.`
      : `${playerLabel} pit ${pitIndex + 1}: ${seeds} seeds`;

    return (
      <button
        key={`${side}-${pitIndex}`}
        className={pitClasses}
        onClick={() => handlePitClick(pitIndex)}
        disabled={!isValid || animating}
        aria-label={ariaLabel}
        role="gridcell"
        tabIndex={isValid ? 0 : -1}
      >
        <span className="mancala-board__pit-count">{seeds}</span>
        {renderSeeds(seeds)}
      </button>
    );
  };

  const renderStore = (side: number) => {
    const seeds = board.stores[side];
    const playerLabel = side === 0 ? 'Player 1' : 'Player 2';
    const isActive =
      (currentPlayer === 'player1' && side === 0) ||
      (currentPlayer === 'player2' && side === 1);

    const storeClasses = [
      'mancala-board__store',
      `mancala-board__store--${side === 0 ? 'right' : 'left'}`,
      isActive ? 'mancala-board__store--active' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        className={storeClasses}
        role="status"
        aria-label={`${playerLabel} store: ${seeds} seeds`}
      >
        <span className="mancala-board__store-label">{playerLabel}</span>
        <span className="mancala-board__store-count">{seeds}</span>
        {renderSeeds(seeds)}
      </div>
    );
  };

  return (
    <div
      className="mancala-board"
      role="grid"
      aria-label="Mancala game board"
    >
      {/* Player 2's store (left side) */}
      {renderStore(1)}

      {/* Pits area */}
      <div className="mancala-board__pits">
        {/* Player 2's pits (top row, displayed right-to-left: 5,4,3,2,1,0) */}
        <div className="mancala-board__row mancala-board__row--top" role="row">
          <span className="mancala-board__row-label" aria-hidden="true">P2</span>
          {Array.from({ length: PITS_PER_SIDE }, (_, i) => {
            const pitIndex = PITS_PER_SIDE - 1 - i; // Reverse order for display
            return renderPit(1, pitIndex);
          })}
        </div>

        {/* Pit numbers */}
        <div className="mancala-board__pit-numbers" aria-hidden="true">
          <span className="mancala-board__row-label" />
          {Array.from({ length: PITS_PER_SIDE }, (_, i) => (
            <span key={i} className="mancala-board__pit-number">
              {i + 1}
            </span>
          ))}
        </div>

        {/* Player 1's pits (bottom row, displayed left-to-right: 0,1,2,3,4,5) */}
        <div className="mancala-board__row mancala-board__row--bottom" role="row">
          <span className="mancala-board__row-label" aria-hidden="true">P1</span>
          {Array.from({ length: PITS_PER_SIDE }, (_, i) =>
            renderPit(0, i)
          )}
        </div>
      </div>

      {/* Player 1's store (right side) */}
      {renderStore(0)}
    </div>
  );
};

export { MancalaBoard };
export default MancalaBoard;
